import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import {
  Button,
  Checkbox,
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerHeaderTitle,
  Dropdown,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Option,
  Switch,
  Textarea
} from '@fluentui/react-components';
import { Trans, useTranslation } from 'react-i18next';

// models
import {
  IdentifierTypeCreateCommand,
  IdentifierTypeUpdateCommand,
  JuridictionIdentifierTypeEnum
} from '../../../models/Entities/Person/IdentifierType/IdentifierTypeModels';

// components
import { ErrorMessageComponent } from '../../Error/ErrorMessageComponent';

// functions
import * as CommonFunctions from '../Persons.Functions';
import * as ownFunctions from './IdentifierType.Form.Functions';

interface FormValidation {
  success: boolean;
  errors: string[];
}

const IdentifierTypeFormComponent = (props: ownFunctions.Props) => {
  const [t] = useTranslation();

  const [submitted, setSubmitted] = useState<boolean>(false);
  const [validation, setValidation] = useState<FormValidation | undefined>();

  const [name, setName] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [juridictionType, setJuridictionType] = useState<JuridictionIdentifierTypeEnum>(
    JuridictionIdentifierTypeEnum.National
  );
  const [isForNaturalPerson, setIsForNaturalPerson] = useState<boolean>(true);
  const [isForJuridicalPerson, setIsForJuridicalPerson] = useState<boolean>(false);
  const [identifierMask, setIdentifierMask] = useState<string>('');
  const [validationRegex, setValidationRegex] = useState<string>('');
  const [isActive, setIsActive] = useState<boolean>(true);

  useEffect(() => {
    if (props.identifierType) {
      setName(props.identifierType.name || '');
      setDescription(props.identifierType.description || '');
      setJuridictionType(props.identifierType.juridictionType ?? JuridictionIdentifierTypeEnum.National);
      setIsForNaturalPerson(props.identifierType.isForLegalPerson || false);
      setIsForJuridicalPerson(props.identifierType.isForJuridicalPerson || false);
      setIdentifierMask(props.identifierType.identifierMask || '');
      setValidationRegex(props.identifierType.validationRegex || '');
      setIsActive(props.identifierType.isActive ?? true);
    } else {
      resetForm();
    }
  }, [props.identifierType, props.isOpen]);

  useEffect(() => {
    if (submitted && (props.successAdding || props.successUpdating)) {
      props.GetIdentifierTypes();
      resetForm();
      props.dismissPanel();
    }
  }, [props.successAdding, props.successUpdating]);

  const resetForm = () => {
    setName('');
    setDescription('');
    setJuridictionType(JuridictionIdentifierTypeEnum.National);
    setIsForNaturalPerson(true);
    setIsForJuridicalPerson(false);
    setIdentifierMask('');
    setValidationRegex('');
    setIsActive(true);
    setSubmitted(false);
    setValidation(undefined);
  };

  const validate = (): boolean => {
    const errors: string[] = [];

    if (!name.trim()) {
      errors.push(t('persons.identifierTypes.form.validation.name', { defaultValue: 'El nombre es obligatorio' }));
    }

    if (!isForNaturalPerson && !isForJuridicalPerson) {
      errors.push(
        t('persons.identifierTypes.form.validation.personType', {
          defaultValue: 'Debe aplicar al menos a un tipo de persona'
        })
      );
    }

    if (validationRegex.trim()) {
      try {
        // eslint-disable-next-line no-new
        new RegExp(validationRegex.trim());
      } catch (error) {
        errors.push(
          t('persons.identifierTypes.form.validation.regex', {
            defaultValue: 'La expresión regular de validación no es válida'
          })
        );
      }
    }

    const result: FormValidation = { success: errors.length === 0, errors };
    setValidation(result);
    return result.success;
  };

  const handleSave = () => {
    setSubmitted(true);
    if (!validate()) return;

    const payload: IdentifierTypeCreateCommand = {
      name: name.trim(),
      description: description.trim() || null,
      juridictionType,
      isForNaturalPerson,
      isForJuridicalPerson,
      identifierMask: identifierMask.trim() || null,
      validationRegex: validationRegex.trim() || null,
      isActive
    };

    if (props.identifierType) {
      props.UpdateIdentifierType({ ...payload, id: props.identifierType.id } as IdentifierTypeUpdateCommand);
    } else {
      props.CreateIdentifierType(payload);
    }
  };

  const handleCancel = () => {
    props.dismissPanel();
    resetForm();
  };

  const selectedJuridiction = CommonFunctions.JuridictionTypeOptions.find(
    (option) => option.key === juridictionType
  );

  const juridictionText = (option: { textKey: string; defaultText: string }): string =>
    String(t(option.textKey, { defaultValue: option.defaultText }));

  return (
    <Drawer
      open={props.isOpen}
      type="overlay"
      position="end"
      size="small"
      onOpenChange={(_, data) => {
        if (!data.open) handleCancel();
      }}
    >
      <DrawerHeader>
        <DrawerHeaderTitle>
          {props.identifierType
            ? t('persons.identifierTypes.form.editTitle', { defaultValue: 'Editar tipo de identificación' })
            : t('persons.identifierTypes.form.addTitle', { defaultValue: 'Nuevo tipo de identificación' })}
        </DrawerHeaderTitle>
      </DrawerHeader>

      <DrawerBody>
        {submitted && validation && !validation.success && (
          <MessageBar intent="error" layout="multiline" style={{ marginBottom: 12 }}>
            <MessageBarBody>
              <MessageBarTitle>
                {t('persons.form.review', { defaultValue: 'Revise la información ingresada' })}
              </MessageBarTitle>
              <ul>
                {validation.errors.map((error: string, index: number) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </MessageBarBody>
          </MessageBar>
        )}

        {submitted && (props.failAdding || props.failUpdating) && props.error && (
          <ErrorMessageComponent Error={props.error} />
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Field
            label={t('persons.identifierTypes.form.name', { defaultValue: 'Nombre' })}
            required
            validationMessage={
              submitted && !name.trim()
                ? t('persons.identifierTypes.form.validation.name', { defaultValue: 'El nombre es obligatorio' })
                : undefined
            }
            validationState={submitted && !name.trim() ? 'error' : undefined}
          >
            <Input value={name} onChange={(_, data) => setName(data.value || '')} />
          </Field>

          <Field label={t('persons.identifierTypes.form.description', { defaultValue: 'Descripción' })}>
            <Textarea value={description} onChange={(_, data) => setDescription(data.value || '')} />
          </Field>

          <Field label={t('persons.identifierTypes.form.juridictionType', { defaultValue: 'Jurisdicción' })}>
            <Dropdown
              value={selectedJuridiction ? juridictionText(selectedJuridiction) : ''}
              selectedOptions={[String(juridictionType)]}
              onOptionSelect={(_, data) =>
                setJuridictionType(Number(data.optionValue) as JuridictionIdentifierTypeEnum)
              }
            >
              {CommonFunctions.JuridictionTypeOptions.map((option) => (
                <Option key={option.key} value={String(option.key)} text={juridictionText(option)}>
                  {juridictionText(option)}
                </Option>
              ))}
            </Dropdown>
          </Field>

          <Checkbox
            label={t('persons.identifierTypes.form.isForNaturalPerson', {
              defaultValue: 'Aplica a personas físicas'
            })}
            checked={isForNaturalPerson}
            onChange={(_, data) => setIsForNaturalPerson(!!data.checked)}
          />

          <Checkbox
            label={t('persons.identifierTypes.form.isForJuridicalPerson', {
              defaultValue: 'Aplica a personas jurídicas'
            })}
            checked={isForJuridicalPerson}
            onChange={(_, data) => setIsForJuridicalPerson(!!data.checked)}
          />

          <Field
            label={t('persons.identifierTypes.form.identifierMask', { defaultValue: 'Máscara' })}
            hint={t('persons.identifierTypes.form.identifierMaskHint', { defaultValue: 'Ejemplo: ##-########-#' })}
          >
            <Input value={identifierMask} onChange={(_, data) => setIdentifierMask(data.value || '')} />
          </Field>

          <Field
            label={t('persons.identifierTypes.form.validationRegex', {
              defaultValue: 'Expresión regular de validación'
            })}
          >
            <Input value={validationRegex} onChange={(_, data) => setValidationRegex(data.value || '')} />
          </Field>

          <Switch
            label={t('persons.identifierTypes.form.isActive', { defaultValue: 'Activo' })}
            checked={isActive}
            onChange={(_, data) => setIsActive(!!data.checked)}
          />
        </div>
      </DrawerBody>

      <DrawerFooter>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-start', width: '100%' }}>
          <Button appearance="secondary" onClick={handleCancel}>
            <Trans i18nKey={'persons.form.footer.cancel'} defaultValue="Cancelar" />
          </Button>
          <Button appearance="primary" onClick={handleSave} disabled={props.isAdding || props.isUpdating}>
            <Trans i18nKey={'persons.form.footer.save'} defaultValue="Guardar" />
          </Button>
        </div>
      </DrawerFooter>
    </Drawer>
  );
};

export default connect(
  ownFunctions.mapStateToProps,
  ownFunctions.mapDispatchToProps
)(IdentifierTypeFormComponent as any);
